import * as Yup from 'yup';
import { Box, Button, Pagination, SxProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { API } from '@aws-amplify/api';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';

import useStudentList from '@common/hooks/useStudentList';
import { FormProvider, RHFDatePicker, RHFSelect, RHFTextField } from '@common/components/hook-form';
import { SimpleCrudFilters } from '@common/@types/crud';
import LoadingScreen from '@common/components/LoadingScreen';
import RadialGaugeLearningSessionFormatter from '@common/components/daily-activity/formatters/learningSession/RadialGaugeLearningSessionFormatter';
import { merged_time_and_activity } from '@common/@types/prisma';
import { trpc } from '@common/utils/trpcAmplify';
import { formatDayjs } from '@common/utils/formatTime';
import { Student } from '@common/utils/amplify/getStudents';
import { Role } from '@common/@types/auth';
import dayjs from '@common/@types/dayjs-custom';

type StudentSelect = {
  value: string;
  key: string;
  label: string;
};

type TandaInput = {
  fromDate: string;
  toDate: string;
  studentIds: string[];
};

const yupValidDateCheck = (value: string | null | undefined) =>
  value == null || dayjs(value, 'YYYY-MM-DD', true).isValid();

const SimpleCrudFiltersSchema = Yup.object().shape({
  campus_id: Yup.string(),
  student_id: Yup.string(),
  full_id: Yup.string(),
  qc_status: Yup.string(),
  fromDate: Yup.mixed<string>()
    .nullable()
    .test('valid dayjs', 'date should be dayjs object', yupValidDateCheck)
    .test(
      'isBeforeOrSame',
      `'From Date' must be equal to/before 'To Date'`,
      function (value: string | null | undefined) {
        const fromDate = value ? dayjs(value, 'YYYY-MM-DD', true) : null;
        const toDateValue: string = this.resolve(Yup.ref('toDate'));
        const toDate = toDateValue ? dayjs(toDateValue, 'YYYY-MM-DD', true) : null;
        return (
          fromDate == null ||
          toDate == null ||
          fromDate.isBefore(toDate, 'date') ||
          fromDate.isSame(toDate, 'date')
        );
      }
    ),
  toDate: Yup.mixed<string>()
    .nullable()
    .test('valid dayjs', 'date should be dayjs object', yupValidDateCheck)
    .test(
      'isAfterOrSame',
      `'To Date' must be equal to/after 'From Date'`,
      function (value: string | null | undefined) {
        const toDate = value ? dayjs(value, 'YYYY-MM-DD', true) : null;
        const fromDateValue: string = this.resolve(Yup.ref('fromDate'));
        const fromDate = fromDateValue ? dayjs(fromDateValue, 'YYYY-MM-DD', true) : null;
        return (
          toDate == null ||
          fromDate == null ||
          toDate.isAfter(fromDate, 'date') ||
          toDate.isSame(fromDate, 'date')
        );
      }
    ),
  subject: Yup.string(),
  start: Yup.number().min(0),
  number: Yup.number().min(1),
});

export function SimpleCrudListFilters({
  total = 0,
  onSubmit,
  showQC = true,
  overrideCampusFilter = false, // AI-GEN - Cursor - GPT4
}: {
  total?: number;
  onSubmit: (data: string) => void;
  showQC?: boolean;
  overrideCampusFilter?: boolean; // AI-GEN - Cursor - GPT4
}) {
  // Prep student list
  const studentList = useStudentList(overrideCampusFilter); // AI-GEN - Cursor - GPT4
  const students = studentList?.map((student: any) => ({
    value: student.student_id,
    key: student.student_id,
    label: student.name,
  }));

  // useForm setup
  const methods = useForm<SimpleCrudFilters>({
    resolver: yupResolver(SimpleCrudFiltersSchema),
    defaultValues: {
      campus_id: '',
      student_id: '',
      subject: '',
      start: 0,
      number: 10,
      fromDate: formatDayjs(dayjs().subtract(1, 'day')),
      toDate: formatDayjs(dayjs()),
    },
  });
  const { getValues, handleSubmit, setValue } = methods;

  // Splits student_id into campus_id
  const preProcessSubmit = (data: SimpleCrudFilters) => {
    const payload = {
      ...data,
      campus_id: data.full_id != null ? data.full_id.split('-')[0] : undefined,
      student_id: data.full_id != null ? data.full_id.split('-')[1] : undefined,
    };
    onSubmit(JSON.stringify(payload));
  };

  // Update filters on the parent once
  useEffect(() => {
    const values = getValues();
    preProcessSubmit(values);
  }, [getValues]);

  const start = getValues('start') as number;
  const number = getValues('number') as number;

  // Triggers a load
  function handlePaginationChange(event: React.ChangeEvent<unknown>, value: number) {
    setValue('start', (value - 1) * number);
    preProcessSubmit(getValues());
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(preProcessSubmit)}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'baseline' }}>
        <RHFSelect name="full_id" label="Student" sx={{ marginRight: '12px', flex: '0 1 auto' }}>
          <option value="" />
          {students?.map((student: StudentSelect) => (
            <option key={student.value} value={student.value}>
              {student.label}
            </option>
          ))}
        </RHFSelect>

        <RHFDatePicker
          name="fromDate"
          label="From Date"
          sx={{ marginRight: '12px', flex: '0 1 auto' }}
        />
        <RHFDatePicker
          name="toDate"
          label="To Date"
          sx={{ marginRight: '12px', flex: '0 1 auto' }}
        />
        <RHFTextField
          name="subject"
          label="Subject"
          sx={{ marginRight: '12px', flex: '0 1 auto' }}
        />

        {showQC && (
          <RHFSelect name="qc_status" label="QC Status" sx={{ marginRight: '12px' }}>
            <option value="" />
            <option value="ready">Ready</option>
            <option value="fail">Fail</option>
            <option value="pass">Pass</option>
          </RHFSelect>
        )}

        <Button type="submit" variant="contained" sx={{ flex: '1 0 auto', paddingY: '12px' }}>
          Update Filters
        </Button>
      </Box>

      <Box sx={{ marginY: '6px' }}>
        <Pagination
          count={Math.ceil(total / number)}
          size="small"
          page={start / number + 1}
          onChange={handlePaginationChange}
        />
      </Box>
    </FormProvider>
  );
}

export function SimpleCrudRadialView({
  data = [],
  subject,
  isLoading,
  sticky = true,
  sx,
}: {
  data: merged_time_and_activity[] | undefined;
  subject: string;
  isLoading: boolean;
  sticky?: boolean;
  sx?: SxProps;
}) {
  return (
    <Box
      sx={{
        display: 'block',
        marginLeft: '24px',
        position: sticky ? 'sticky' : undefined,
        top: '72px',
        height: '400px',
        width: '275px', // ensures there's enough width for the loading screen
        '.MuiCardContent-root': {
          paddingBottom: '24px !important', // because someone put !important on the card itself
        },
        ...(sx ?? {}),
      }}
    >
      {isLoading ? (
        <LoadingScreen positionFixed={false} />
      ) : (
        <RadialGaugeLearningSessionFormatter
          learningSession={data?.find((learningDay: any) => learningDay.subject === subject)}
          onClick={() => {}}
          isLoading={false}
        />
      )}
    </Box>
  );
}

export function SimpleCrudSidebar({
  disableQCStatus,
  data = [],
  subject,
  isLoading,
  role,
  anyRole = false,
  showQC = true,
}: {
  disableQCStatus?: boolean | undefined;
  data: merged_time_and_activity[] | undefined;
  subject: string | undefined;
  isLoading: boolean;
  role: Role | undefined;
  anyRole?: boolean;
  showQC?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'block',
        flexDirection: 'column',
        alignItems: 'center',
        marginLeft: '24px',
        position: 'sticky',
        top: '72px',
        width: '275px', // ensures there's enough width for the loading screen
        '.MuiCardContent-root': {
          paddingBottom: '24px !important', // because someone put !important on the card itself
        },
      }}
    >
      {showQC && (
        <>
          <RHFSelect
            disabled={disableQCStatus}
            name="qc_status"
            label="QC Status"
            sx={{ marginTop: '15px' }}
          >
            <option value="" />
            <option value="ready">Ready</option>
            <option value="fail">Fail</option>
            <option value="pass">Pass</option>
          </RHFSelect>
          <RHFTextField
            name="qc_explanation"
            label="QC Explanation"
            sx={{ marginY: '21px' }}
            disabled={role !== Role.ADMIN && !anyRole}
            multiline
          />
        </>
      )}

      {subject ? (
        isLoading ? (
          <LoadingScreen positionFixed={false} />
        ) : (
          <RadialGaugeLearningSessionFormatter
            learningSession={data?.find((learningDay: any) => learningDay.subject === subject)}
            onClick={() => {}}
            isLoading={false}
          />
        )
      ) : null}
    </Box>
  );
}

export function prepDataForSubmit(data: PostIdConfig, studentList?: Student[]) {
  // Pre-processing
  const splitStudentId = data.student_id?.split('-') || [];
  if (splitStudentId.length === 2) {
    data.campus_id = splitStudentId[0];
    data.student_id = splitStudentId[1];
  }
  data.level = studentList?.find((student: any) => {
    let candidateStudentId: string;
    if (student.student_id.indexOf('-') >= 0) {
      candidateStudentId = student.student_id.split('-')[1];
    } else {
      candidateStudentId = student.student_id;
    }
    return candidateStudentId === data.student_id;
  })?.alpha_level;
  return data;
}

// Requirements for TypeScript
type PostIdConfig = {
  level?: string;
  student_id?: string;
  campus_id?: string;
};
export function useDashDataLoader<TBaseDataType extends PostIdConfig>(input: {
  methods: any; // has to stay any or you enter a TypeScript nightmare
  studentList: Student[] | undefined;
  isNew: boolean;
  urlBase: string;
  path: string;
}) {
  const { methods, studentList, isNew, urlBase, path } = input;
  const { getValues, watch } = methods;
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useRouter();

  // This is complex but really cool: set placeholder objects for the two objects
  // we send to the mergedTimeAndActivity query
  const [tandaInput, setTandaInput] = useState<TandaInput | undefined>();
  const [tandaEnabled, setTandaEnabled] = useState<boolean>(false);

  // Set up a useEffect that relies on `watch()` for the props we need to build
  // the input object
  useEffect(() => {
    // We only want to make the query if both date and student_id have values
    const studentId = getValues('student_id');
    const enabled = !!getValues('learning_session_date') && !!studentId;
    setTandaEnabled(enabled);
    if (!enabled) return;

    // If they do both have values, set the input, and...
    const dateString = dayjs.isDayjs(getValues('learning_session_date'))
      ? formatDayjs(getValues('learning_session_date'))
      : getValues('learning_session_date');

    // All the possible ways ID could show up:
    const campusId = getValues('campus_id');
    const id = campusId && studentId.indexOf('-') === -1 ? `${campusId}-${studentId}` : studentId;
    setTandaInput({
      fromDate: dateString as string,
      toDate: dateString as string,
      studentIds: [id],
    });
  }, [watch('learning_session_date'), watch('student_id'), watch('campus_id')]);

  // ...it'll trigger a load here.
  const { data: learningDayData, isLoading: learningDayDataIsLoading } =
    trpc.mergedTimeAndActivity.get.useQuery(tandaInput, {
      enabled: tandaEnabled,
    });

  // ...but we need to add a useState call for the selected one.
  const [subject, setSubject] = useState<string | undefined>();
  useEffect(() => {
    const subject = getValues('subject');
    setSubject(subject);
  }, [watch('subject')]);

  const onSubmit = async (data: TBaseDataType) => {
    try {
      if (!data.student_id) return; // should never happen because of coalesce above
      prepDataForSubmit(data, studentList);
      const apiName = process.env.NEXT_PUBLIC_ACADEMICS_API_NAME!;
      let response;

      if (isNew) response = await API.post(apiName, path, { body: data });
      else response = await API.put(apiName, path, { body: data });
      // Always point back to the view page
      push(urlBase + '?id=' + response.id);
      enqueueSnackbar('Success!');
    } catch (error) {
      enqueueSnackbar(`Error saving. Please try again later.`, { variant: 'error' });
      console.error(error);
    }
  };

  return {
    onSubmit,
    learningDayDataIsLoading,
    learningDayData,
    subject,
  };
}
